import { resolvePromotionsProgramEnabled } from 'pi-kiosk-shared/promo/resolvePromotionsProgramEnabled';
import {
  resolveAccountHomeEntitlements,
  type AccountHomeEntitlements,
} from '../account/presenters/accountHomeScreenPresenter.js';
import type { CustomerMembershipsDTO } from '../account/accountTypes.js';

export interface CheckoutIncentiveSurface extends AccountHomeEntitlements {
  /** Tenant promotions ∧ sales-point promotionsProgramEnabled — promo API / pricing path. */
  readonly promoPricingEnabled: boolean;
  /** Mount typed promo when tenant promotions on (kiosk parity — SP-off shows disabled field + copy). */
  readonly showTypedPromoBlock: boolean;
  /** Combined promo discount preview (SP-gated). */
  readonly showPromoPreview: boolean;
  readonly showLoyaltyPreview: boolean;
  /** Either loyalty or tenant promotions program active — exclusive modal / pay lock. */
  readonly incentiveProgramsEnabled: boolean;
  readonly promotionsProgramEnabled: boolean;
}

/** Hook pay-lock mirror of modal gate (G4-A / G4-B / G7). */
export function resolveCheckoutIncentiveConflict(input: {
  readonly incentiveProgramsEnabled: boolean;
  readonly hasCheckoutSession: boolean;
  readonly isDonationSession: boolean;
  readonly showExclusiveModal: boolean;
  readonly mutualExclusion: boolean;
}): boolean {
  return (
    input.incentiveProgramsEnabled &&
    input.hasCheckoutSession &&
    !input.isDonationSession &&
    (input.showExclusiveModal || input.mutualExclusion)
  );
}

/** Same inputs as usePhoneFirstCheckoutScreen checkoutIncentiveSurface useMemo (G5). */
export function buildCheckoutIncentiveSurfaceFromHookDeps(input: {
  readonly memberships: CustomerMembershipsDTO | undefined;
  readonly loyaltyProgramEntitled: boolean;
  readonly promotionsProgramEntitled: boolean;
  readonly commerceCapabilitiesPromotionsProgramEnabled?: boolean | null;
  readonly commerceConfigJson?: unknown;
}): CheckoutIncentiveSurface {
  return resolveCheckoutIncentiveSurface({
    memberships: input.memberships,
    entitlementBlocks: {
      loyalty_program: input.loyaltyProgramEntitled,
      promotions_program: input.promotionsProgramEntitled,
    },
    commerceCapabilitiesPromotionsProgramEnabled:
      input.commerceCapabilitiesPromotionsProgramEnabled,
    commerceConfigJson: input.commerceConfigJson,
  });
}

export function resolveCheckoutIncentiveSurface(input: {
  readonly memberships: CustomerMembershipsDTO | undefined;
  readonly entitlementBlocks: {
    readonly loyalty_program: boolean;
    readonly promotions_program: boolean;
  };
  readonly commerceCapabilitiesPromotionsProgramEnabled?: boolean | null;
  readonly commerceConfigJson?: unknown;
}): CheckoutIncentiveSurface {
  const { promotionsEnabled, loyaltyEnabled, walletVisible } = resolveAccountHomeEntitlements(
    input.memberships,
    input.entitlementBlocks,
  );
  const promotionsProgramEnabled = resolvePromotionsProgramEnabled({
    commerceCapabilitiesPromotionsProgramEnabled:
      input.commerceCapabilitiesPromotionsProgramEnabled,
    commerceConfigJson: input.commerceConfigJson,
  });
  const promoPricingEnabled = promotionsEnabled && promotionsProgramEnabled;

  return {
    promotionsEnabled,
    loyaltyEnabled,
    walletVisible,
    promotionsProgramEnabled,
    promoPricingEnabled,
    // Kiosk parity (G1-PARITY): block stays mounted when tenant promos on; SP-off → disabled apply in block/hook.
    showTypedPromoBlock: promotionsEnabled,
    showPromoPreview: promoPricingEnabled,
    showLoyaltyPreview: loyaltyEnabled,
    incentiveProgramsEnabled: promotionsEnabled || loyaltyEnabled,
  };
}
